import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { withStyles } from 'tss-react/mui';
import { useContext, useState } from 'react';
import { ITreeSection } from '../../../../../../BaseComponents/TreeSelect/TreeSelectUtils';
import {
  ActiveDatabaseContext,
  IActiveDatabaseContext,
} from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { IUIUrval, IUrvalCondition } from '../../../../../../Types/UrvalTypes';
import { RutinSettingsContext } from '../../../../_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import { MeQuery } from '../Queries/MeQuery/_QueryTypes/MeQuery';
import resursQuery from '../Queries/ResursQuery/ResursQuery.gql';
import {
  ResursQuery,
  ResursQueryVariables,
} from '../Queries/ResursQuery/_QueryTypes/ResursQuery';
import UrvalDialog from '../../../../../../Components/Urval/Components/UrvalDialog/UrvalDialog';
import { rutinInfos } from '../../../../_Common/Const/RutinInfo';
import RutinUrvalListQuery from '../../../../../../Components/Urval/Components/SavedUrval/Queries/RutinUrvalListQuery/RutinUrvalListQuery';
import {
  copySectionOpen,
  createResursPageFromResources,
  createResursPageFromSections,
  getCheckedResourcesFromUrval,
  KalenderResourceTreeSelect,
  recursiveUpdateCheckedResources,
} from './KalenderResourceTreeSelect';
import { ActiveRoutineContextProvider } from '../../../../../Routing/ActiveRoutine/ActiveRoutineContext';
import { IResourceInfo } from '../Utils/KalenderTypes';
import InfoText from '../../../../../../Components/InfoText/InfoText';
import {
  EDAutoCompleteEdit,
  ISuggestion,
} from '../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';
import {
  convertResourceToResourceInfo,
  getDefaultBookingGroupName,
  getLoggedInResource,
} from '../Utils/ResourceUtils';
import { LoginStateContext } from '../../../../../Login/Contexts/LoginStateContext/LoginStateContext';
import mergeSort from '../../../../../../Utils/mergeSort';
import {
  IResursTypContext,
  ResursTypContext,
} from '../../../Contexts/ResursTypContext/ResursTypContext';
import { isEmpty } from 'ramda';
import { BusinessCollectionMenuItem } from '../../../../_Common/Components/BusinessCollectionComponents/BusinessCollectionMenu/Components/BusinessCollectionMenuItem';
import { ViewDbSettingsContext } from './SchedulerComponent/ViewDbSettingsContext';
import { IRenderButtonProps } from '../../../../_Common/Const/Delsystem';

interface IProps {
  onSelect: (checkedResources: IResourceInfo[]) => void;
  routineID: number | undefined;
  activeDataBaseContext: IActiveDatabaseContext;
  me_data: MeQuery | undefined;
  me_loading: boolean;
  newBookingGroup: string;
  setNewBookingGroup?: (newBookingGroup: string) => void;
  expanded?: boolean;
  renderButton?: (props: IRenderButtonProps) => React.ReactNode;
  classes?: any;
}

const styles = () => ({
  dialogPaper: {
    width: 'calc(100% - 64px)',
  },
  dialogHeaderTopArea: {
    paddingRight: 0,
  },

  bokningsGruppContainer: {
    display: 'flex',
    textAlign: 'center',
  },
});

const getSubList = (
  resourceData: ResursQuery,
  resursTypContext: IResursTypContext,
  checkedResources: string[],
  data: string
): ITreeSection[] => {
  let subList: ITreeSection[] = [];

  const resourcesBaseSubTypes = resursTypContext.resTypes();

  resourcesBaseSubTypes?.forEach(item => {
    if ((item.foraldratyp || '') === data) {
      const resTypes =
        getSubList(
          resourceData,
          resursTypContext,
          checkedResources,
          item.namn
        ) ?? [];
      const resources = mergeSort(
        resourceData?.db?.BOResurs_All ?? [],
        (a, b) => (a.PRIO_ORDNING ?? 1000) - (b.PRIO_ORDNING ?? 1000)
      ).reduce<ITreeSection[]>((acc, item2) => {
        if (
          item2._Associations?.ResRestyp.BO_RESURSTYP_NAMN === item.namn &&
          item2.Resursnamn
        ) {
          acc.push({
            name: item2.Resursnamn,
            value: item2.BO_RESURS_ID,
            id: item2.BO_RESURS_ID,
            checked:
              checkedResources.find(res => res === item2.BO_RESURS_ID) !==
              undefined,
          });
        }
        return acc;
      }, []);

      subList.push({
        name: item.namn,
        value: item.id,
        subList: [...resTypes, ...resources],
        id: item.id,
        open: data === '',
      });
    }
  });

  let filtered = subList.filter(item => item.subList?.length !== 0);
  return filtered;
};

export const convertResourceIDsToResourceInfos = (
  checkedResourceIDs: string[],
  userSign: string,
  resurs_data: ResursQuery | undefined
): IResourceInfo[] =>
  checkedResourceIDs
    .map((resId: string) => {
      const resource = resurs_data?.db?.BOResurs_All?.find(
        resItem => resItem.BO_RESURS_ID === resId
      );

      const resourceInfo = resource
        ? convertResourceToResourceInfo(resource, userSign)
        : undefined;

      return {
        resId: resourceInfo?.resId || '',
        resName: resourceInfo?.resName || '',
        BO_RESURS_ID: resourceInfo?.BO_RESURS_ID || '',
        resType: resourceInfo?.resType || '',
        resGQLId: resourceInfo?.resGQLId || '',
        basTyp: resourceInfo?.basTyp || '',
        platsForsamlingId: resourceInfo?.platsForsamlingId,
        kanBoka: resourceInfo?.kanBoka || false,
      };
    })
    .filter(item => item.resId);

const KalenderResourceSelectDialog: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const loginStateContext = useContext(LoginStateContext);
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const routineAvailabilityContext = useContext(RutinSettingsContext);
  const modelContext = useContext(ModelContext);
  const viewDbSettingsContext = useContext(ViewDbSettingsContext);
  const resursTypContext = useContext(ResursTypContext);
  const [sections, setSections] = useState<ITreeSection[]>([]);
  const [urval, setUrval] = useState<IUIUrval | null>(() => {
    if (viewDbSettingsContext.resources) {
      return {
        pages: [
          createResursPageFromResources(
            modelContext,
            viewDbSettingsContext.resources
          ),
        ],
      };
    } else return null;
  });
  const [open, setOpen] = useState<boolean>(false);
  const [ordinarieBokningsGrupp, setOrdinarieBokningsGrupp] =
    useState<string>('');

  const userBookingID = props.me_data?.me?.[0]?.userBookingID;
  const routineID = routineAvailabilityContext.activeRoutine?.routine.rutinID;

  const currentBokningsgrupp =
    props.newBookingGroup === ''
      ? getDefaultBookingGroupName(props.me_data)
      : props.newBookingGroup;

  const { data: resurs_data } = useQuery<ResursQuery, ResursQueryVariables>(
    resursQuery,
    {
      variables: {
        dbname: props.activeDataBaseContext.activeDatabase ?? '',
        routineID: routineID,
        selectedBokningsgrupp: currentBokningsgrupp ?? '',
      },
      skip:
        !open ||
        !currentBokningsgrupp ||
        !activeDatabaseContext.activeDatabase ||
        props.me_loading,
    }
  );

  const checkedResources = useMemo(() => {
    const loggedInResource = resurs_data
      ? getLoggedInResource(loginStateContext.sign, resurs_data)
      : undefined;

    return getCheckedResourcesFromUrval(urval, loggedInResource);
  }, [loginStateContext, resurs_data, urval]);

  const bokningsgrupper =
    props.me_data?.me?.[0]?.bokningsgrupper?.map(item => ({
      name: item.namn,
      value: item.id,
    })) ?? [];

  useEffect(() => {
    if (urval && resurs_data) {
      const sortOrder = ['Plats', 'Person', 'Övrigt'];
      let resourcesBaseItems = (
        getSubList(resurs_data, resursTypContext, [], '') || []
      ).sort((a, b) => sortOrder.indexOf(a.name) - sortOrder.indexOf(b.name));
      if (isEmpty(resourcesBaseItems))
        resourcesBaseItems = [
          { name: 'Inga resurser hittades', value: '', id: '' },
        ];

      const newSections = recursiveUpdateCheckedResources(
        resourcesBaseItems,
        checkedResources
      );

      const newSectionsWithOpenPreserved = copySectionOpen(
        newSections,
        sections
      );

      setSections(newSectionsWithOpenPreserved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resurs_data, checkedResources]);

  useEffect(() => {
    if (props.me_data?.me) {
      const ordinarieBokningsGrupp =
        props.me_data.me[0]?.bokningsgrupper?.find(
          bokningsgrupp => bokningsgrupp.ordinarie
        )?.namn ?? '';

      setOrdinarieBokningsGrupp(ordinarieBokningsGrupp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.me_data?.me?.[0]?.bokningsgrupper]);

  const onChange = (name: string) => {
    if (props.setNewBookingGroup) {
      props.setNewBookingGroup(name);
    }

    setOrdinarieBokningsGrupp(name);
  };

  const getCurrentBookingGroupId = (
    me_data: MeQuery | undefined,
    ordinarieBokningsGrupp: string
  ) => {
    let bookingGroupId = '';
    me_data?.me?.map(x => {
      x.bokningsgrupper?.map(item => {
        if (item.namn === ordinarieBokningsGrupp) {
          bookingGroupId = item.id;
        }
      });
    });

    return bookingGroupId;
  };

  useEffect(() => {
    const condition = urval?.pages[0]?.conditionObjects[0]?.condition as
      | IUrvalCondition[]
      | undefined;
    if (
      condition?.[0]?.values?.length !== undefined &&
      condition?.[0]?.values?.length <= 1 &&
      condition?.[0]?.values[0]?.value === 'Signatur' &&
      !userBookingID
    ) {
      setOpen(true);
    }
  }, [urval, userBookingID]);

  return (
    <>
      {!props.me_loading && routineID && (
        <ActiveRoutineContextProvider
          collectionName="BOResurs"
          rutinInfo={rutinInfos.rtnResurser}
        >
          {props.children}

          <RutinUrvalListQuery
            collectionName={'BOResurs'}
            routineID={routineID}
            kodplanUrval={false}
          >
            {({ rutinUrvalList, variables }) => (
              <UrvalDialog
                routineID={routineID}
                setUrval={setUrval}
                propagateSetUrval
                collectionName={'BOResurs'}
                urval={urval}
                open={open}
                onClose={() => {
                  setOpen(false);
                }}
                onCommit={() => {
                  props.onSelect(
                    convertResourceIDsToResourceInfos(
                      checkedResources,
                      props.me_data?.me?.[0]?.sign ?? '',
                      resurs_data
                    )
                  );

                  routineAvailabilityContext.setCurrentBookingGroup(
                    props.newBookingGroup
                  );

                  setOpen(false);
                }}
                kodplanUrval={false}
                rutinUrvalList={rutinUrvalList}
                rutinUrvalVariables={variables}
                kodbehorighet={
                  <div className={props.classes.bokningsGruppContainer}>
                    <InfoText
                      text={`Bokningsgrupp: ${
                        props.newBookingGroup === ''
                          ? ordinarieBokningsGrupp
                          : props.newBookingGroup
                      }`}
                      tooltipText="Visa bokningsgrupper"
                      dialogTitle={`Bokningsgrupper `}
                      textColor="inherit"
                      buttonColor="inherit"
                      buttonMargin="none"
                      buttonPadding="normal"
                      buttonNoBottomMargin={false}
                      buttonNoTopMargin={false}
                    >
                      <EDAutoCompleteEdit<ISuggestion>
                        label=""
                        options={bokningsgrupper}
                        value={{
                          name:
                            props.newBookingGroup === ''
                              ? ordinarieBokningsGrupp
                              : props.newBookingGroup,
                          value: getCurrentBookingGroupId(
                            props.me_data,
                            props.newBookingGroup === ''
                              ? ordinarieBokningsGrupp
                              : props.newBookingGroup
                          ),
                        }}
                        onChange={(
                          option: { name: string; value: string } | null
                        ) => {
                          onChange(option?.name ?? '');
                        }}
                        getOptionLabel={option => {
                          return option.name || '';
                        }}
                        getOptionSelected={(option, value) =>
                          option.value === value.value
                        }
                        fullWidth
                      />
                    </InfoText>
                  </div>
                }
                overrideUrvalSidaComp={(page, setPage) => {
                  return (
                    <KalenderResourceTreeSelect
                      dbname={props.activeDataBaseContext.activeDatabase ?? ''}
                      routineID={routineID}
                      sections={sections}
                      setPage={setPage}
                      newBookingGroup={props.newBookingGroup}
                    />
                  );
                }}
                leftFooterActions={[
                  {
                    label: 'Rensa',
                    onClick: () => {
                      const newCheckedResources: string[] = [];
                      const newSections = recursiveUpdateCheckedResources(
                        sections,
                        newCheckedResources
                      );

                      setSections(newSections);
                      setUrval({
                        pages: [
                          createResursPageFromSections(
                            modelContext,
                            newSections
                          ),
                        ],
                      });
                    },
                    buttonProps: {
                      disabled:
                        checkedResources[0] === '_NoRes' ||
                        checkedResources.length === 0 ||
                        (checkedResources[1] === 'Signatur' &&
                          checkedResources[0] === ''),
                    },
                  },
                ]}
                disableActionButton={
                  checkedResources[0] === '_NoRes' ||
                  checkedResources.length === 0 ||
                  (checkedResources[1] === 'Signatur' &&
                    checkedResources[0] === '')
                }
                hideUrvalHitIndicator={false}
              />
            )}
          </RutinUrvalListQuery>
        </ActiveRoutineContextProvider>
      )}
      {props.renderButton ? (
        props.renderButton({
          onClick: () => setOpen(true),
          icon: 'resources_filter',
          label: 'Välj resurser',
        })
      ) : (
        <BusinessCollectionMenuItem
          expanded={props.expanded}
          icon="resources_filter"
          tooltip="Välj resurser"
          onClick={() => {
            setOpen(true);
          }}
          testId={'BusinessCollectionMenu_Resources'}
        />
      )}
    </>
  );
};

export default withStyles(KalenderResourceSelectDialog, styles);
